<script lang="ts" context="module">
    import CartIcon from "../widgets/CartIcon.svelte"
    import { icons } from "../../config"
    import { getTableColumn } from "../../getTableColumns"
    import { get } from "svelte/store"
    import { _ } from "svelte-i18n"
    export const getOperation1TableColumns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
        ...getTableColumn(_showPrices, "operation1"),
    ]

    export const getOperation2TableColumns: (_showPrices: boolean) => TableColumn[] = (_showPrices) => [
        ...getTableColumn(_showPrices, "operation2"),
    ]

    export const getOperationButtons = (type: number, _showPrices?: boolean, account?: Account) => {
        const buttons: TableButton<"operation | operation-out">[] = [
            {
                label: get(_)("operationProtocol.details"),
                className: "btn_details",
                svgPath: mdiPageNext,
                onClick: openOverlay,
                getTooltip(operation) {
                    const i18n = get(_)
                    return {
                        content: i18n("operations.showPositionDetails"),
                    }
                },
            },
            {
                hide: !_showPrices,
                label: get(_)("operations.inquiry"),
                className: "inquiry ",
                svgPath: icons.inquiry,
                onClick(op) {
                    openOverlay(op, "inquiry", {
                        operation: op,
                    })
                },
                getTooltip(op) {
                    const i18n = get(_)
                    return {
                        content: op.type == 1 ? i18n("operations.offer") : i18n("operations.order"),
                    }
                },
            },
        ]

        if (!type || (type === 1 && (account?.roles?.includes("A") || account?.permissions?.includes("B")))) {
            const i18n = get(_)
            buttons.push({
                label: i18n("cart.cart"),
                className: "cart",
                component: CartIcon,
                disabled(op) {
                    return op?.statusSummary?.created !== 100 && op.collectionName == "operation"
                },
                onClick(op) {},
                getComponentProps(op) {
                    return {
                        operation: op,
                    }
                },
            })
        }

        return buttons
    }
    export const getFollowUpButton = (
        type: number,
        _showPrices?: boolean,
        account?: Account
    ): TableButton<"operation | operation-out"> => {
        const i18n = get(_)
        return {
            label: i18n("header.watchlist"),
            className: "follow-up",
            component: FollowUpButton,
            getComponentProps(op) {
                return {
                    operation: op,
                    personNumber: account.ident.personNumber,
                }
            },
        }
    }
</script>

<script lang="ts">
    import { getQueryParam, requireLogin } from "../../helper"
    import {
        login,
        location,
        openOverlay,
        overlayTabs,
        pendingOverlayOperation,
        showPrices,
        newNotification,
    } from "../../store"
    import { mdiPageNext } from "@mdi/js"
    import Table from "../widgets/Table.svelte"
    import { getOperation } from "../../api"
    import OperationsFilter from "./operations/OperationsFilter.svelte"
    import OperationsLegend from "./operations/OperationsLegend.svelte"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import OperationTabNavi from "./operations/OperationTabNavi.svelte"
    import FollowUpButton from "../widgets/FollowUpButton.svelte"
    import TableTop from "./operations/TableTop.svelte"

    export let lang = "de"
    let gridview = false
    let innerWidth = window.innerWidth
    const gridBreakPoint = 600
    let filteropen = false

    let type: number
    let limit = 20
    let offset = 0
    let filter: any = {}
    let rows: Operation[] = []
    let showMoreButton = false
    let loading = false
    let sort = "-number"

    let userFilter: []
    let resetFilter: () => void

    requireLogin()

    let abortController: AbortController

    function load() {
        if (abortController) {
            abortController.abort() // cancel all running requests
        }
        abortController = new AbortController()
        const { signal } = abortController

        loading = true
        getOperation("operation", filter, limit, offset, "list", sort, signal)
            .then((r) => {
                rows = [...rows, ...(r.data as Operation[])]
                offset += limit
                showMoreButton = limit == r.data.length
            })
            .catch((e) => {})
            .finally(() => {
                loading = false
            })
    }

    $: if ($location.search || sort || userFilter) {
        type = parseInt(getQueryParam("type"))
        $overlayTabs = null
        filter = {}
        filter.type = type
        if (userFilter) {
            filter["$and"] = userFilter
        }
        offset = 0
        rows = []
        if (type) load()
    }

    // Separater Handler für Kurzlink-Pending — darf nicht in den location-Block,
    // sonst würde $pendingOverlayOperation einen Reload der Liste triggern oder
    // bei gleichem ?type gar nicht feuern (gleiche URL -> $location.search ändert sich nicht).
    // Reviewer-Vorschlag `|| $pendingOverlayOperation` im obigen Block ist daher Quatsch.
    $: if ($pendingOverlayOperation) {
        const pendingOp = $pendingOverlayOperation
        pendingOverlayOperation.set(null)
        // Falls wir bereits auf der Liste waren, wurde $overlayTabs oben nicht zurückgesetzt
        $overlayTabs = null
        openOverlay(pendingOp)
    }

    let columns: TableColumn[]
    $: {
        columns = type == 1 ? getOperation1TableColumns($showPrices) : getOperation2TableColumns($showPrices)
    }
</script>

<svelte:window bind:innerWidth />

<section class="section_padding" data-lang="{lang}">
    <div class="container_wide">
        <OperationTabNavi {type} />

        <OperationsFilter bind:active="{filteropen}" bind:processedFilter="{userFilter}" {type} bind:resetFilter />

        <TableTop {userFilter} bind:resetFilter {gridBreakPoint} bind:gridview bind:filteropen />

        {#if $login}
            <Table
                className="operationtable {type == 2 ? 'table_auftraege' : ''}"
                gridview="{gridview || innerWidth <= gridBreakPoint}"
                bind:sort
                {columns}
                buttonsColumn="{{
                    label: $_('general.actions'),
                    className: 'rueckfrage mw-120 max-w-150 w-120',
                }}"
                buttons="{getOperationButtons(type, $showPrices, $login?.account)}"
                followUpColumn="{{
                    label: $_('header.watchlist'),
                    className: 'mw-160 max-w-180 w-160',
                }}"
                followUpButton="{getFollowUpButton(type, $showPrices, $login?.account)}"
                {rows}
                on:click="{(r) => {
                    if (
                        r.detail.statusSummary?.created !== 100 &&
                        !$login.account?.roles?.includes('A') &&
                        !$login.account?.roles?.includes('R')
                    ) {
                        newNotification({
                            class: 'error',
                            html: $_('operations.incompleteOperation'),
                        })
                        return
                    }
                    openOverlay(r.detail)
                }}"
            />
        {/if}

        {#if loading}
            <div class="centerbutton">
                <br />
                <br />
                <MiniLoader />
            </div>
        {/if}

        {#if showMoreButton && !loading}
            <!-- Load more -->
            <div class="centerbutton">
                <button class="btn loadmore" on:click="{load}">{$_("general.loadMore")}</button>
            </div>
        {/if}
        <OperationsLegend {type} />
    </div>
</section>
